import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import GaugeChart from "react-gauge-chart";
import * as d3 from "d3";
import {
  BarChart,
  Bar,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import { CircularProgress } from "@mui/material";
import axios from "axios";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";

const API_BASE_URL = "https://genz-staging.feeltiptop.com";

// Score bar component
const ScoreBar = ({ label, score, maxScore = 10 }) => (
  <div className="mb-4">
    <div className="mb-1 flex justify-between text-sm">
      <span className="text-gray-700">{label}</span>
      <span className="font-medium text-gray-900">{score}</span>
    </div>
    <div className="h-2 overflow-hidden rounded-full bg-gray-100">
      <div
        className="h-full bg-green-500"
        style={{
          width: `${(score / maxScore) * 100}%`,
          transition: 'width 0.5s ease-in-out'
        }}
      />
    </div>
  </div>
);

// Horizontal Box component for boxplot
const HorizontalBox = ({
  min,
  q1,
  median,
  q3,
  max,
  height,
  stroke,
  fill,
  userScore,
}) => {
  const STROKE_WIDTH = 1.5;

  return (
    <>
      {/* Whiskers and box */}
      <line
        y1={height / 2}
        y2={height / 2}
        x1={min}
        x2={max}
        stroke={stroke}
        strokeWidth={STROKE_WIDTH}
      />
      <rect
        y={0}
        x={q1}
        height={height}
        width={q3 - q1}
        stroke={stroke}
        fill={fill}
        fillOpacity={0.3}
        strokeWidth={STROKE_WIDTH}
      />
      <line
        y1={0}
        y2={height}
        x1={median}
        x2={median}
        stroke={stroke}
        strokeWidth={STROKE_WIDTH}
      />
      <line
        y1={height * 0.25}
        y2={height * 0.75}
        x1={min}
        x2={min}
        stroke={stroke}
        strokeWidth={STROKE_WIDTH}
      />
      <line
        y1={height * 0.25}
        y2={height * 0.75}
        x1={max}
        x2={max}
        stroke={stroke}
        strokeWidth={STROKE_WIDTH}
      />
      {/* User score indicator */}
      <circle
        cx={userScore}
        cy={height / 2}
        r={6}
        fill="#FF6B6B"
        stroke="white"
        strokeWidth={2}
      />
    </>
  );
};

// Gauge component using react-gauge-chart
const Gauge = ({ value }) => (
  <div className="h-60 w-60">
    <GaugeChart
      id="gauge-chart"
      nrOfLevels={20}
      percent={value / 100}
      colors={["#FF5F6D", "#FFC371", "#4979D1"]}
      arcWidth={0.3}
      textColor="#000000"
      needleColor="#4979D1"
      needleBaseColor="#4979D1"
      animate={false}
    />
    <div className="-mt-4 text-center">
      <span className="text-xl font-bold text-neutral-700">Good</span>
    </div>
  </div>
);

// Dummy data for the distribution
const dummyDistributionData = {
  min: 45,
  q1: 65,
  median: 75,
  q3: 85,
  max: 95,
  userScore: 82,
};

// Placeholder scores for the breakdown
const placeholderScores = {
  "Syntax-Semantics": 4,
  "Temporal Pattern": 10,
  "Fluency and Filler words": 10,
  "Vocabulary Richness": 10,
  "Discourse Cohesion": 7,
};

const CustomBoxplot = ({ width, height }) => {
  const margin = { top: 20, right: 20, left: 40, bottom: 20 };
  const boundsWidth = width - margin.left - margin.right;
  const boundsHeight = height - margin.top - margin.bottom;

  const xScale = useMemo(() => {
    return d3.scaleLinear().domain([0, 100]).range([0, boundsWidth]);
  }, [boundsWidth]);

  // X axis ticks
  const xTicks = xScale.ticks(5).map((tick) => ({
    value: tick,
    xOffset: xScale(tick),
  }));

  // Scale the distribution values
  const scaledData = {
    min: xScale(dummyDistributionData.min),
    q1: xScale(dummyDistributionData.q1),
    median: xScale(dummyDistributionData.median),
    q3: xScale(dummyDistributionData.q3),
    max: xScale(dummyDistributionData.max),
    userScore: xScale(dummyDistributionData.userScore),
  };

  return (
    <svg width={width} height={height}>
      <g transform={`translate(${margin.left}, ${margin.top})`}>
        {/* X axis */}
        <g transform={`translate(0, ${boundsHeight})`}>
          {xTicks.map(({ value, xOffset }) => (
            <g key={value} transform={`translate(${xOffset}, 0)`}>
              <line y1={0} y2={5} stroke="#6b7280" strokeWidth={0.5} />
              <text y={20} textAnchor="middle" fontSize={10} fill="#6b7280">
                {value}
              </text>
            </g>
          ))}
        </g>

        {/* Distribution labels */}
        <text
          x={-10}
          y={boundsHeight / 2}
          textAnchor="end"
          fontSize={12}
          fill="#4B5563"
          dy=".32em"
        >
          Score Distribution
        </text>

        {/* Boxplot */}
        <g transform={`translate(0, ${boundsHeight / 3})`}>
          <HorizontalBox
            min={scaledData.min}
            q1={scaledData.q1}
            median={scaledData.median}
            q3={scaledData.q3}
            max={scaledData.max}
            height={40}
            stroke="#4979D1"
            fill="#4979D1"
            userScore={scaledData.userScore}
          />
        </g>

        {/* Legend */}
        <g transform={`translate(${boundsWidth - 100}, 10)`}>
          <circle
            cx={0}
            cy={0}
            r={6}
            fill="#FF6B6B"
            stroke="white"
            strokeWidth={2}
          />
          <text x={12} y={0} dy=".32em" fontSize={10} fill="#4B5563">
            Your Score
          </text>
        </g>
      </g>
    </svg>
  );
};

/**
 * AudioPlayer: Custom audio player component with loading state and authentication
 */
const AudioPlayer = ({ src, label }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);

  React.useEffect(() => {
    const loadAudio = async () => {
      if (!src) {
        setIsLoading(false);
        return;
      }

      console.log("Loading audio from:", src); // Debug log

      try {
        // If it's already a full URL or a data URL, use it directly
        if (src.startsWith("http") || src.startsWith("data:")) {
          console.log("Using direct URL:", src);
          setAudioUrl(src);
          setIsLoading(false);
          return;
        }

        // If it's a media path (either media/ or /media/), fetch through our API
        if (src.includes("media/")) {
          const token = localStorage.getItem("authToken");
          // Clean up the path to ensure proper format
          let cleanPath = src.replace(/^\/+/, ""); // Remove leading slashes only
          const fullUrl = `${API_BASE_URL}/api/audio/${cleanPath}`;
          console.log("Fetching audio from:", fullUrl); 

          const response = await axios.get(fullUrl, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            responseType: "blob",
          });

          console.log(
            "Audio response:",
            response.status,
            response.headers["content-type"],
          );

          const audioBlob = new Blob([response.data], {
            type: response.headers["content-type"] || "audio/wav",
          });
          const blobUrl = URL.createObjectURL(audioBlob);
          console.log("Created blob URL:", blobUrl);
          setAudioUrl(blobUrl);
        } else {
          // If it's not a valid URL or media path, show error
          console.error("Invalid audio source:", src);
          setError("Invalid audio source");
        }
      } catch (err) {
        console.error("Error loading audio:", err.response || err);
        setError(err.response?.data?.message || "Failed to load audio");
      } finally {
        setIsLoading(false);
      }
    };

    loadAudio();

    // Cleanup function to revoke object URL
    return () => {
      if (
        audioUrl &&
        !audioUrl.startsWith("http") &&
        !audioUrl.startsWith("data:")
      ) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [src]);

  if (!src) {
    return (
      <div className="rounded-lg bg-gray-50 p-4 text-center text-gray-500">
        No audio available
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-gray-50 p-4">
      {isLoading && (
        <div className="flex h-12 items-center justify-center">
          <CircularProgress size={24} />
        </div>
      )}
      {error && <div className="text-center text-red-500">{error}</div>}
      {audioUrl && (
        <>
          <audio
            controls
            src={audioUrl}
            className={`w-full ${isLoading ? "hidden" : ""}`}
            onError={(e) => {
              console.error("Audio element error:", e);
              setError("Failed to play audio");
            }}
          />
        </>
      )}
      {label && <p className="mt-2 italic text-gray-700">{label}</p>}
    </div>
  );
};

/**
 * SentenceBreakdownPage: shows individual task breakdown, audio playback, analysis, and metrics.
 */
export default function SentenceBreakdownPage({ taskData }) {
  const [selectedTask, setSelectedTask] = useState(0);
  const currentTask = taskData[selectedTask];

  // Debug log for task data
  React.useEffect(() => {
    console.log("Current task data:", currentTask);
  }, [currentTask]);

  // Calculate overall score for the current sentence
  const overallScore = useMemo(() => {
    if (!currentTask || !currentTask.scores) return 0;
    // If only one score and it's Correctness, scale it
    if (
      Object.keys(currentTask.scores).length === 1 &&
      currentTask.scores.Correctness !== undefined
    ) {
      const score = currentTask.scores.Correctness;
      return typeof score === "number" && score <= 1 && score >= 0
        ? Math.round(score * 100) / 10
        : Math.round(score * 10) / 10;
    }
    const values = Object.entries(currentTask.scores).map(([label, score]) =>
      label === "Correctness Score" &&
      typeof score === "number" &&
      score <= 1 &&
      score >= 0
        ? score * 10
        : score,
    );
    if (values.length === 0) return 0;
    const total = values.reduce((sum, v) => sum + v, 0);
    return Math.round((total / values.length) * 10) / 10;
  }, [currentTask]);

  return (
    <div className="h-screen bg-[#4979D1] p-5">
      <div className="flex h-[calc(100vh-40px)]">
        {/* Combined Task List and Main Content */}
        <div className="mr-5 flex flex-1 overflow-hidden rounded-lg bg-white">
          {/* Task List */}
          <div className="w-48 border-r border-gray-200">
            {taskData.map((task, index) => (
              <button
                key={task.id}
                onClick={() => setSelectedTask(index)}
                className={`relative w-full px-4 py-3 text-left transition hover:bg-gray-50 ${
                  selectedTask === index
                    ? "border-r-2 border-r-blue-500 bg-gray-50 font-medium text-blue-500"
                    : "text-gray-700"
                }`}
              >
                Sentence {index + 1}
              </button>
            ))}
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto p-8">
            <div className="max-w-3xl">
              <section className="mb-8">
                <h2 className="mb-4 text-left text-2xl font-bold">
                  Original Sentence
                </h2>
                <p className="mb-2 text-sm text-gray-600">
                  played the sentence {currentTask.original.playCount} times
                  before recording
                </p>
                <AudioPlayer
                  src={currentTask.original.audioUrl}
                  label={`"${currentTask.original.text}"`}
                />
              </section>

              <section className="mb-8">
                <h2 className="mb-4 text-left text-2xl font-bold">
                  Patient's Recording
                </h2>
                <AudioPlayer src={currentTask.recording.audioUrl} />
              </section>

              <section>
                <h2 className="mb-4 text-left text-2xl font-bold">
                  Sentence Analysis
                </h2>
                <div className="rounded-lg bg-white p-6 shadow-sm">
                  <div className="space-y-4">
                    {currentTask.analysis
                      .split("\n")
                      .map((paragraph, index) => {
                        if (paragraph.startsWith("Recommendations:")) {
                          return (
                            <div
                              key={index}
                              className="mt-6 border-t border-gray-200 pt-4"
                            >
                              <h3 className="mb-2 text-lg font-semibold text-blue-600">
                                Recommendations
                              </h3>
                              <ul className="list-disc space-y-2 pl-5">
                                {paragraph
                                  .replace("Recommendations:", "")
                                  .split(".")
                                  .filter((rec) => rec.trim())
                                  .map((rec, i) => (
                                    <li key={i} className="text-gray-700">
                                      {rec.trim()}
                                    </li>
                                  ))}
                              </ul>
                            </div>
                          );
                        }
                        return (
                          <p
                            key={index}
                            className="leading-relaxed text-gray-700"
                          >
                            {paragraph}
                          </p>
                        );
                      })}
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>

        {/* Right Panel */}
        <div className="w-96 rounded-lg bg-gray-50 p-8">
          <h2 className="mb-6 text-2xl font-bold text-gray-800">
            Overall Performance
          </h2>
          <div className="flex flex-col items-center">
            <div className="h-40 w-40">
              <CircularProgressbar
                value={overallScore * 10}
                text={`${Math.round(overallScore * 10)}%`}
                styles={buildStyles({
                  textColor: "#222",
                  pathColor: "#4979D1",
                  trailColor: "#e5e7eb",
                  textSize: "20px",
                  pathTransitionDuration: 0.5,
                })}
              />
            </div>
            <div className="mt-2 text-center">
                <span className="text-lg font-bold text-neutral-700">
                  {overallScore >= 7
                    ? "Good"
                    : overallScore >= 5
                      ? "Average"
                      : "Needs Improvement"}
                </span>
              </div>
            {/* <div className="mt-2 h-32 w-full">
              <CustomBoxplot width={300} height={120} />
            </div> */}
            <div className="mt-4 w-full">
              <h3 className="mb-4 text-xl font-bold">Score Breakdown</h3>
              {Object.entries(currentTask.scores).map(([label, score]) => {
                // Only scale 'Correctness' from 0-1 to 0-10
                const displayScore =
                  label === "Correctness Score" &&
                  typeof score === "number" &&
                  score <= 1 &&
                  score >= 0
                    ? score * 10
                    : score;
                return (
                  <ScoreBar key={label} label={label} score={displayScore} />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

AudioPlayer.propTypes = {
  src: PropTypes.string,
  label: PropTypes.string,
};

SentenceBreakdownPage.propTypes = {
  taskData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      original: PropTypes.shape({
        text: PropTypes.string.isRequired,
        audioUrl: PropTypes.string.isRequired,
        playCount: PropTypes.number.isRequired,
      }).isRequired,
      recording: PropTypes.shape({
        audioUrl: PropTypes.string.isRequired,
      }).isRequired,
      analysis: PropTypes.string.isRequired,
      scores: PropTypes.object.isRequired,
    }),
  ).isRequired,
};
